import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Box, Card, CardActionArea, CardContent, IconButton, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTripTags } from "../../admin/dictionaries/useTripTags";
import { CardMediaImg } from "../CardImage";
const TILE_WIDTH = 188;
const TILE_GAP = 12;

function TopicsCarousel() {
  const { tags } = useTripTags();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const animRef = useRef<number | null>(null);
  const navigate = useNavigate();

  const tagsWithImages = tags.filter((tag) => tag.image?.thumbnail?.url);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [updateScrollState, tagsWithImages.length]);

  /** Płynne przewijanie (bez snap / skoków) */
  const scrollByDir = (dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (animRef.current !== null) cancelAnimationFrame(animRef.current);

    const distance = Math.min(el.clientWidth * 0.75, (TILE_WIDTH + TILE_GAP) * 3);
    const start = el.scrollLeft;
    const target = Math.max(0, Math.min(start + dir * distance, el.scrollWidth - el.clientWidth));
    const duration = 420;
    const startTime = performance.now();

    const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;

    const step = (now: number) => {
      const t = Math.min(1, (now - startTime) / duration);
      el.scrollLeft = start + (target - start) * easeOutCubic(t);
      if (t < 1) {
        animRef.current = requestAnimationFrame(step);
      } else {
        animRef.current = null;
        updateScrollState();
      }
    };
    animRef.current = requestAnimationFrame(step);
  };

  const navBtnSx = {
    width: 28,
    height: 28,
    border: "1px solid rgba(0,0,0,0.12)",
    bgcolor: "#fff",
    color: "text.secondary",
    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
    "&.Mui-disabled": { opacity: 0.35 },
  };

  if (tagsWithImages.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 5 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
        Polecane tematy
      </Typography>
      <Box
        ref={scrollerRef}
        sx={{
          display: "flex",
          gap: `${TILE_GAP}px`,
          overflowX: "auto",
          scrollBehavior: "auto",
          pb: 0.5,
          mx: { xs: -0.5, md: 0 },
          px: { xs: 0.5, md: 0 },
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
      >
        {tagsWithImages.map((tag) => (
          <Card
            key={tag.id}
            elevation={0}
            sx={{
              flex: `0 0 ${TILE_WIDTH}px`,
              width: TILE_WIDTH,
              borderRadius: 1,
              border: "1px solid rgba(0,0,0,0.08)",
              overflow: "hidden",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
              },
            }}
          >
            <CardActionArea
              onClick={(e) => {
                e.preventDefault();
                navigate(`filter?tags=${tag.category}::${tag.name}`);
              }}
              sx={{ height: "100%" }}
            >
              <Box sx={{ position: "relative" }}>
                <CardMediaImg
                  component="img"
                  height="100"
                  imageUrl={tag.image?.thumbnail?.url}
                  alt={tag.image?.name}
                  sx={{ objectFit: "cover" }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 65%)",
                  }}
                />
              </Box>
              <CardContent sx={{ p: 1.25, "&:last-child": { pb: 1.25 } }}>
                <Stack direction="row" spacing={0.5} sx={{ mb: 0.35, alignItems: "flex-start" }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.25, fontSize: "0.8rem", fontWeight: 700 }}>
                    {tag.name}
                  </Typography>
                </Stack>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <Stack direction="row" spacing={0.75} sx={{ mt: 1, justifyContent: "flex-end" }}>
        <IconButton
          size="small"
          aria-label="Przewiń w lewo"
          disabled={!canScrollLeft}
          onClick={() => scrollByDir(-1)}
          sx={navBtnSx}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <IconButton
          size="small"
          aria-label="Przewiń w prawo"
          disabled={!canScrollRight}
          onClick={() => scrollByDir(1)}
          sx={navBtnSx}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>
    </Box>
  );
}

export default TopicsCarousel;
